import { crqAnalysisRuleStatus } from 'utils/ignoramusEnums';

// Analysis Rule Status Messages
export const STATUS_MESSAGES = {
  [crqAnalysisRuleStatus.COMPLETED.key]: 'It is ready!',
  [crqAnalysisRuleStatus.RUNNING.key]: 'Analysis rule check in progress. This may take a few minutes.',
  [crqAnalysisRuleStatus.QUEUED.key]: 'Analysis rule check in progress. This may take a few minutes.',
  [crqAnalysisRuleStatus.FAILED.key]: 'This question run failed because dataset analysis rules were not met.',
};

// Failure Type Constants
export const FAILURE_TYPES = {
  PARSING_ERROR: 'PARSING_ERROR',
  RESOLUTION_ERROR: 'RESOLUTION_ERROR',
  PROJECTION_CONSTRAINT: 'PROJECTION_CONSTRAINT',
  PERMITTED_JOIN_CONSTRAINT: 'PERMITTED_JOIN_CONSTRAINT',
  MANDATORY_JOIN_CONSTRAINT: 'MANDATORY_JOIN_CONSTRAINT',
  KMIN_CONSTRAINT: 'KMIN_CONSTRAINT',
  NOT_SUPPORTED: 'NOT_SUPPORTED',
};

// KMIN Constraint Failure Sub-types
export const KMIN_FAILURE_TYPES = {
  CONSTRAINT_FAILURE: 'CONSTRAINT_FAILURE',
  UNKNOWN: 'UNKNOWN',
  NOT_SUPPORTED: 'NOT_SUPPORTED',
};

// Failure Type Titles
export const FAILURE_TYPE_TITLES = {
  [FAILURE_TYPES.PARSING_ERROR]: 'SQL Could Not be Parsed',
  [FAILURE_TYPES.RESOLUTION_ERROR]: 'Field References Not Valid',
  [FAILURE_TYPES.PROJECTION_CONSTRAINT]: 'Projection Error',
  [FAILURE_TYPES.PERMITTED_JOIN_CONSTRAINT]: 'Join Error',
  [FAILURE_TYPES.MANDATORY_JOIN_CONSTRAINT]: 'Join Requirement Not Met',
  [FAILURE_TYPES.KMIN_CONSTRAINT]: 'Aggregation Threshold Not Met',
  [FAILURE_TYPES.NOT_SUPPORTED]: 'SQL Uses Unsupported Feature',
};

// KMIN Constraint Failure Sub-type Titles
export const KMIN_FAILURE_TYPE_TITLES = {
  [KMIN_FAILURE_TYPES.CONSTRAINT_FAILURE]: 'Aggregation Threshold Exceeded',
  [KMIN_FAILURE_TYPES.UNKNOWN]: 'Transient Error',
  [KMIN_FAILURE_TYPES.NOT_SUPPORTED]: 'Multiple Fields with Aggregation Thresholds',
};

/**
 * Remove generated ID suffix from table names
 * Example: crm_id535e2a0520b54f5cb1d9dbeae61999bd -> crm
 */
const cleanTableName = (tableName) => {
  if (!tableName) return tableName;
  return tableName.replace(/_id[a-f0-9]{32}$/i, '');
};

/**
 * Build error message for PROJECTION_CONSTRAINT failure
 */
const buildProjectionConstraintMessage = (projectionFailure) => {
  const cleanTable = cleanTableName(projectionFailure.sourceTable);

  // Check if disallowed functions exist and are not empty
  const hasDisallowedFunctions = projectionFailure.disallowedFunctionsInLineage
    && projectionFailure.disallowedFunctionsInLineage.length > 0
    && projectionFailure.disallowedFunctionsInLineage.some(func => func && func.trim());

  if (hasDisallowedFunctions) {
    const disallowedFuncs = projectionFailure.disallowedFunctionsInLineage
      .filter(func => func && func.trim())
      .join(', ');
    return `Column '${cleanTable}.${projectionFailure.sourceColumn}' has been projected as '${projectionFailure.projectedField}' using disallowed functions: [${disallowedFuncs}].`;
  }

  return `Column '${cleanTable}.${projectionFailure.sourceColumn}' has been projected as '${projectionFailure.projectedField}' which is not permitted for projection.`;
};

/**
 * Build error message for PERMITTED_JOIN_CONSTRAINT failure
 */
const buildPermittedJoinConstraintMessage = (permittedJoinFailure) => {
  const cleanTable = cleanTableName(permittedJoinFailure.sourceTable);
  return `Column '${permittedJoinFailure.sourceColumn}' from table '${cleanTable}' is not permitted for join.`;
};

/**
 * Build error message for MANDATORY_JOIN_CONSTRAINT failure
 */
const buildMandatoryJoinConstraintMessage = (mandatoryJoinFailure) => {
  const cleanTable = cleanTableName(mandatoryJoinFailure.table);
  return `Table '${cleanTable}' must be joined on.`;
};

/**
 * Build error message for KMIN_CONSTRAINT failure
 */
const buildKminConstraintMessage = (kminFailure) => {
  const { kminFailureType, missingAggregationThreshold } = kminFailure;

  if (kminFailureType === KMIN_FAILURE_TYPES.CONSTRAINT_FAILURE && missingAggregationThreshold) {
    const cleanTable = cleanTableName(missingAggregationThreshold.table);
    return `Query must include a 'HAVING COUNT(DISTINCT ${cleanTable}.${missingAggregationThreshold.column}) >= ${missingAggregationThreshold.kminThreshold}' clause to meet minimum group size requirements.`;
  }

  if (kminFailureType === KMIN_FAILURE_TYPES.NOT_SUPPORTED) {
    return 'This query structure is not supported for aggregation threshold enforcement.';
  }

  if (kminFailureType === KMIN_FAILURE_TYPES.UNKNOWN) {
    return 'An unexpected error occurred during aggregation threshold validation.';
  }

  // Fallback
  return kminFailure.errorMessage || 'Aggregation threshold validation failed.';
};

export const getFailureTitle = (failure) => {
  if (
    failure.failureType === FAILURE_TYPES.KMIN_CONSTRAINT
    && failure.kminFailure?.kminFailureType
  ) {
    return KMIN_FAILURE_TYPE_TITLES[failure.kminFailure.kminFailureType]
      || FAILURE_TYPE_TITLES[FAILURE_TYPES.KMIN_CONSTRAINT];
  }

  // Return title from mapping or fallback to the failure type itself
  return FAILURE_TYPE_TITLES[failure.failureType] || failure.failureType;
};

export const getFailureMessage = (failure) => {
  const { failureType } = failure;

  // Types use errorMessage directly from backend
  if (
    failureType === FAILURE_TYPES.PARSING_ERROR
    || failureType === FAILURE_TYPES.RESOLUTION_ERROR
    || failureType === FAILURE_TYPES.NOT_SUPPORTED
  ) {
    return failure.errorMessage || '';
  }

  // Other Failure Types
  if (failureType === FAILURE_TYPES.PROJECTION_CONSTRAINT && failure.projectionFailure) {
    return buildProjectionConstraintMessage(failure.projectionFailure);
  }

  if (failureType === FAILURE_TYPES.PERMITTED_JOIN_CONSTRAINT && failure.permittedJoinFailure) {
    return buildPermittedJoinConstraintMessage(failure.permittedJoinFailure);
  }

  if (failureType === FAILURE_TYPES.MANDATORY_JOIN_CONSTRAINT && failure.mandatoryJoinFailure) {
    return buildMandatoryJoinConstraintMessage(failure.mandatoryJoinFailure);
  }

  if (failureType === FAILURE_TYPES.KMIN_CONSTRAINT && failure.kminFailure) {
    return buildKminConstraintMessage(failure.kminFailure);
  }

  return failure.errorMessage || '';
};

export const deduplicateFailures = (failures) => {
  const seenMessages = new Set();
  return failures.filter((failure) => {
    const message = getFailureMessage(failure);
    if (seenMessages.has(message)) {
      return false;
    }
    seenMessages.add(message);
    return true;
  });
};
